import React, { useState } from 'react';
import { useAffiliate } from '../hooks/useAffiliate';
import { Link as LinkIcon, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';

interface AffiliateButtonProps {
  toolId: string;
  websiteUrl: string;
}

export function AffiliateButton({ toolId, websiteUrl }: AffiliateButtonProps) {
  const { loading, createAffiliateLink, getAffiliateStatus } = useAffiliate();
  const { isAuthenticated } = useAuth();
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [affiliateLink, setAffiliateLink] = useState<string>('');

  const handleCreateLink = async () => {
    try {
      const status = await getAffiliateStatus();
      if (!status || !status.id) {
        toast.error('Vous devez être affilié pour créer un lien');
        return;
      }

      const link = await createAffiliateLink(toolId);
      if (!link || !link.code) {
        throw new Error('Erreur lors de la création du lien');
      }
      
      const fullLink = `https://novster.fr/tools/${link.code}`;
      setAffiliateLink(fullLink);
      setShowLinkModal(true);
    } catch (err) {
      console.error('Error creating affiliate link:', err);
      toast.error('Erreur lors de la création du lien');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(affiliateLink);
    toast.success('Lien copié !');
  };

  // If not authenticated, just show the normal website link
  if (!isAuthenticated) {
    return (
      <a
        href={websiteUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium"
      >
        <LinkIcon className="w-5 h-5" />
        <span>Visiter le site</span>
      </a>
    );
  }

  return (
    <>
      <button
        onClick={handleCreateLink}
        disabled={loading}
        className="flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium"
      >
        <LinkIcon className="w-5 h-5" />
        <span>Créer un lien d'affiliation</span>
      </button>

      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Votre lien d'affiliation</h3>
            <div className="flex items-center gap-2 mb-6">
              <input
                type="text"
                value={affiliateLink}
                readOnly
                className="flex-1 px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
              />
              <button
                onClick={copyToClipboard}
                className="p-2 text-gray-500 hover:text-orange-500"
                title="Copier le lien"
              >
                <Copy className="w-5 h-5" />
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowLinkModal(false)}
                className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}